// Social-media share sheet: renders a 1:1 (feed) or 9:16 (story) recipe
// card as PNG + AI caption, then opens the native share sheet.
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import { Loader2, Share2, Copy, RefreshCw, Sparkles } from "lucide-react";
import { api } from "@/lib/http";
import { Capacitor } from "@capacitor/core";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const isNative = (() => { try { return Capacitor.isNativePlatform(); } catch { return false; } })();

const TEAL = "#1F5C5F";
const ORANGE = "#E8951C";
const CREAM = "#FAF4E6";
const INK = "#1A1D1E";
const SIZES = { square: { w: 1080, h: 1080 }, portrait: { w: 1080, h: 1350 }, story: { w: 1080, h: 1920 } };
const PHOTO_H = { square: 560, portrait: 660, story: 960 };
const FORMAT_LABEL = { square: "recipes.socialFormatSquare", portrait: "recipes.socialFormatPortrait", story: "recipes.socialFormatStory" };
const FORMAT_HINT = { square: "recipes.socialFormatHintSquare", portrait: "recipes.socialFormatHintPortrait", story: "recipes.socialFormatHintStory" };
const MEAL_EMOJI = { colazione: "🥐", spuntino: "🍎", pranzo: "🥗", merenda: "🍪", cena: "🍽️" };

function loadImg(src) {
  return new Promise((res, rej) => {
    const im = new Image();
    if (!src.startsWith("data:")) im.crossOrigin = "anonymous";
    im.onload = () => res(im);
    im.onerror = (e) => rej(e);
    im.src = src;
  });
}

// Draw the photo (cover-fit) under the rasterized card; the card's photo area is transparent.
async function composeWithPhoto(cardPng, photoSrc, format) {
  const { w, h } = SIZES[format];
  const [card, photo] = await Promise.all([loadImg(cardPng), loadImg(photoSrc)]);
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  const ctx = c.getContext("2d");
  ctx.fillStyle = CREAM; ctx.fillRect(0, 0, w, h);
  const rh = PHOTO_H[format];
  const s = Math.max(w / photo.naturalWidth, rh / photo.naturalHeight);
  const dw = photo.naturalWidth * s, dh = photo.naturalHeight * s;
  ctx.save(); ctx.beginPath(); ctx.rect(0, 0, w, rh); ctx.clip();
  ctx.drawImage(photo, (w - dw) / 2, (rh - dh) / 2, dw, dh);
  ctx.restore();
  ctx.drawImage(card, 0, 0, w, h);
  return c.toDataURL("image/png");
}

async function toDataUri(url) {
  try {
    const resp = await fetch(url, { cache: "force-cache", credentials: "include" });
    if (!resp.ok) return url;
    const blob = await resp.blob();
    return await new Promise((res, rej) => {
      const fr = new FileReader();
      fr.onload = () => res(fr.result);
      fr.onerror = rej;
      fr.readAsDataURL(blob);
    });
  } catch {
    return url;
  }
}

// Recipe photos are ~1.2 MB base64; WebKit fails to load the resulting SVG
// data-URL ("[object Event]"). Re-encode to ≤1080px JPEG (~150 KB) first.
async function shrinkPhoto(src, max = 1080) {
  if (!src) return null;
  try {
    const img = new Image();
    if (!src.startsWith("data:")) img.crossOrigin = "anonymous";
    img.decoding = "async";
    await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = src; });
    const r = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
    const c = document.createElement("canvas");
    c.width = Math.max(1, Math.round(img.naturalWidth * r));
    c.height = Math.max(1, Math.round(img.naturalHeight * r));
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    return c.toDataURL("image/jpeg", 0.85);
  } catch {
    return src;
  }
}

function SocialCard({ recipe, format, labels, assets }) {
  const { w, h } = SIZES[format];
  const story = format === "story";
  const tall = story || format === "portrait";
  // Instagram/TikTok overlay UI covers ~250px top & bottom of a 1080x1920 story → keep content inside the safe zone.
  const photoH = PHOTO_H[format];
  const tiles = [
    [recipe.calories_per_serving, "kcal", ""],
    [recipe.protein, labels.protein, " g"],
    [recipe.carbs, labels.carbs, " g"],
    [recipe.fat, labels.fat, " g"],
  ].filter(([v]) => v != null && v !== "");
  const titleSize = story ? 64 : tall ? 56 : 52;
  return (
    <div id="social-share-card" style={{
      width: w, height: h, boxSizing: "border-box", overflow: "hidden", background: assets.photo ? "transparent" : CREAM, color: INK,
      fontFamily: "'Inter','Segoe UI',system-ui,-apple-system,sans-serif", display: "flex", flexDirection: "column", position: "relative",
    }}>
      <div style={{ width: w, height: photoH, position: "relative", background: assets.photo ? "transparent" : `linear-gradient(135deg, ${TEAL}, #5A8B8F)`, flexShrink: 0 }}>
        {/* The photo itself is composed onto the canvas afterwards (WebKit fails to rasterize big <img> in foreignObject). */}
        {!assets.photo && (
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: story ? 360 : 240 }}>
            {MEAL_EMOJI[recipe.meal_type] || "🍽️"}
          </div>
        )}
        <div style={{ position: "absolute", inset: 0, background: story ? "linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.25) 30%, rgba(0,0,0,0) 50%)" : "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 35%)" }} />
        <div style={{ position: "absolute", top: story ? 250 : 40, left: 44, background: "rgba(255,255,255,0.92)", borderRadius: 999, padding: "14px 26px", display: "flex", alignItems: "center" }}>
          <img src={assets.logo} alt="Solano Essence" style={{ width: 210, objectFit: "contain", display: "block" }} />
        </div>
        <div style={{ position: "absolute", top: story ? 254 : 44, right: 44, background: ORANGE, color: "#fff", borderRadius: 999, padding: "14px 28px", fontSize: 28, fontWeight: 800 }}>
          {labels.mealType}
        </div>
      </div>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: story ? "56px 72px 0" : "44px 60px 0", minHeight: 0, background: CREAM }}>
        <p style={{ fontSize: titleSize, fontWeight: 900, lineHeight: 1.1, color: TEAL, letterSpacing: "-0.02em", margin: 0, flexShrink: 0,
          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          {recipe.name}
        </p>
        {tall && recipe.description && (
          <p style={{ fontSize: 32, color: "#4B5563", marginTop: 20, lineHeight: 1.35, flexShrink: 0, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {recipe.description}
          </p>
        )}
        <div style={{ display: "flex", gap: story ? 20 : 16, marginTop: story ? 36 : 32, flexShrink: 0 }}>
          {tiles.map(([v, l, unit], i) => (
            <div key={i} style={{ flex: 1, background: "#fff", border: "1px solid rgba(31,92,95,0.15)", borderRadius: 24, padding: story ? "26px 10px" : "22px 8px", textAlign: "center", boxShadow: "0 2px 10px rgba(31,63,65,0.06)" }}>
              <p style={{ fontSize: story ? 52 : 44, fontWeight: 900, color: i === 0 ? ORANGE : INK, margin: 0, lineHeight: 1 }}>{Math.round(Number(v))}{unit}</p>
              <p style={{ fontSize: story ? 24 : 22, fontWeight: 600, color: "#6B7280", margin: "10px 0 0" }}>{l}</p>
            </div>
          ))}
        </div>
        <div style={{ marginTop: "auto", flexShrink: 0, display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "2px solid rgba(31,92,95,0.12)", padding: story ? "26px 0 0" : "18px 0 0" }}>
          <div>
            <p style={{ fontSize: story ? 28 : 26, fontWeight: 800, color: TEAL, margin: 0 }}>{labels.madeWith}</p>
            <p style={{ fontSize: story ? 24 : 22, fontWeight: 600, color: "#6B7280", margin: "6px 0 0" }}>solano-essence.com</p>
          </div>
          <img src={assets.mascot} alt="" style={{ width: story ? 110 : 100, height: story ? 110 : 100, objectFit: "contain" }} />
        </div>
        <div style={{ flexShrink: 0, display: "flex", justifyContent: "center", padding: story ? "22px 0 200px" : tall ? "16px 0 34px" : "12px 0 26px" }}>
          <span style={{ background: TEAL, color: "#fff", borderRadius: 999, padding: story ? "18px 40px" : "14px 32px", fontSize: story ? 30 : 26, fontWeight: 800, letterSpacing: "0.01em" }}>
            {labels.fullRecipe} →
          </span>
        </div>
      </div>
    </div>
  );
}

// Generic sheet. Recipe mode: pass `recipe`. Custom mode: pass `renderCard(format, assets)`
// (must render a node with id="social-share-card"), `captionPayload`, `title`, `slug`, `formats`.
export default function SocialShareSheet({ open, onClose, recipe, lang, t, renderCard, captionPayload, title, slug, formats, mascot = "chef-256.webp", renderKey }) {
  const fmts = formats || ["story", "portrait", "square"];
  const [format, setFormat] = useState(fmts[0]);
  const [assets, setAssets] = useState(null);
  const [caption, setCaption] = useState("");
  const [captionBusy, setCaptionBusy] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const cardRef = useRef(null);
  const subject = title || recipe?.name || "Solano Essence";

  const labels = recipe ? {
    protein: t("cliDash.protein"), carbs: t("cliDash.carbs"), fat: t("cliDash.fat"),
    servings: t("recipes.infographicServings"), madeWith: t("recipes.socialMadeWith"), fullRecipe: t("recipes.socialFullRecipeInApp"),
    mealType: t(`recipes.folder${{ colazione: "Breakfast", spuntino: "Snack", pranzo: "Lunch", merenda: "Afternoon", cena: "Dinner" }[recipe.meal_type] || "Lunch"}`),
  } : null;

  const fetchCaption = async () => {
    setCaptionBusy(true);
    try {
      const payload = captionPayload || {
        kind: "recipe", name: recipe.name, description: recipe.description, calories: recipe.calories_per_serving,
        protein: recipe.protein, carbs: recipe.carbs, fat: recipe.fat, meal_type: recipe.meal_type,
      };
      const { data } = await api.post("/recipes/social-caption", { ...payload, lang });
      setCaption(data.text || "");
    } catch (e) {
      if (e?.response?.status !== 402) toast.error(t("recipes.socialCaptionError"));
      setCaption(`${subject}\n\n#SolanoEssence #HealthyFood #MediterraneanDiet`);
    } finally {
      setCaptionBusy(false);
    }
  };

  useEffect(() => {
    if (!open || (!recipe && !renderCard)) return;
    setCaption("");
    setFormat(fmts[0]);
    setAssets(null);
    const origin = window.location.origin;
    const raw = recipe ? (recipe.image_url || recipe.image_base64 || null) : null;
    setPhotoNote(recipe && !raw ? "no-image" : null);
    // Always pull the photo through our own API (CORS-safe, resized) unless it's already inline.
    const photoSrc = !raw ? null
      : raw.startsWith("data:") ? raw
      : `${process.env.REACT_APP_BACKEND_URL}/api/recipes/${recipe.recipe_id}/image?w=1080&proxy=1`;
    const loadPhoto = async () => {
      if (!photoSrc) return null;
      const uri = photoSrc.startsWith("data:") ? photoSrc : await toDataUri(photoSrc);
      if (!uri) { setPhotoNote("photo-fetch-failed"); return null; }
      if (!uri.startsWith("data:")) { setPhotoNote("photo-fetch-failed: cors"); return null; }
      return shrinkPhoto(uri);
    };
    Promise.all([
      toDataUri(`${origin}/logo-solano-essence.webp`),
      toDataUri(`${origin}/mascot/${mascot}`),
      loadPhoto().catch((e) => { setPhotoNote(`photo-fetch-failed: ${e?.message || e}`); return null; }),
    ]).then(([logo, m, photo]) => setAssets({ logo, mascot: m, photo }));
    fetchCaption();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, recipe?.recipe_id, renderKey]);

  // Pre-render the PNG as soon as assets/format are ready so the share tap
  // only does blob + navigator.share (iOS drops the user-gesture window if
  // we rasterize 1080x1920 after the tap → "NotAllowedError").
  const [png, setPng] = useState({});
  const [renderErr, setRenderErr] = useState(null);
  const [photoNote, setPhotoNote] = useState(null);
  useEffect(() => {
    if (!open || !assets || png[format]) return;
    let cancelled = false;
    (async () => {
      let stage = "render";
      try {
        await new Promise((r) => requestAnimationFrame(() => setTimeout(r, 200)));
        const node = cardRef.current?.querySelector("#social-share-card");
        if (!node) throw new Error("card not rendered");
        const imgs = Array.from(node.querySelectorAll("img"));
        await Promise.all(imgs.map((im) => (im.complete && im.naturalWidth > 0) ? Promise.resolve() : new Promise((res) => {
          im.addEventListener("load", res, { once: true }); im.addEventListener("error", res, { once: true }); setTimeout(res, 3000);
        })));
        await Promise.all(imgs.map((im) => im.decode?.().catch(() => {})));
        const compose = !renderCard && !!assets.photo;
        const opts = { pixelRatio: 1, cacheBust: false, backgroundColor: compose ? undefined : CREAM };
        if (!renderCard) { opts.width = SIZES[format].w; opts.height = SIZES[format].h; }
        const isWebKit = /iPhone|iPad|iPod/.test(navigator.userAgent) || (/^((?!chrome|crios|android).)*safari/i.test(navigator.userAgent));
        if (isWebKit) { await toPng(node, opts); await toPng(node, opts); }
        let dataUrl = await toPng(node, opts);
        if (compose) {
          stage = "compose";
          try {
            dataUrl = await composeWithPhoto(dataUrl, assets.photo, format);
          } catch (e) {
            // Photo could not be drawn (CORS/taint/decode) → keep the card, show why.
            setPhotoNote(`photo-failed: ${e instanceof Event ? e.type : (e?.message || e)}`);
            setAssets((a) => ({ ...a, photo: null }));
            return;
          }
        }
        if (!cancelled) setPng((p) => ({ ...p, [format]: dataUrl }));
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof Event ? `${e.type} (${e.target?.tagName || "img"})` : String(e?.message || e);
        if (assets.photo) { setPhotoNote(`${stage}-failed: ${msg}`); setAssets((a) => ({ ...a, photo: null })); return; }
        setRenderErr(msg);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, assets, format]);
  useEffect(() => { if (!open) { setPng({}); setRenderErr(null); setPhotoNote(null); } }, [open]);

  const copyCaption = async () => {
    try { await navigator.clipboard.writeText(caption); toast.success(t("recipes.socialCopied")); } catch { /* noop */ }
  };

  const share = async () => {
    const dataUrl = png[format];
    if (!dataUrl) return;
    setShareBusy(true);
    const fname = `solano-${format}-${(slug || subject).toLowerCase().replace(/[^a-z0-9äöüß]+/gi, "-").slice(0, 40)}.png`;
    try {
      if (caption) { try { await navigator.clipboard.writeText(caption); } catch { /* noop */ } }
      if (navigator.share && navigator.canShare) {
        const blob = await (await fetch(dataUrl)).blob();
        const file = new File([blob], fname, { type: "image/png" });
        if (navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({ files: [file], text: caption, title: subject });
            toast.success(t("recipes.socialCaptionInClipboard"));
            return;
          } catch (e) {
            if (e?.name === "AbortError") return;
            if (isNative) { toast.error(t("recipes.socialShareFailedHint")); return; }
          }
        }
      }
      if (isNative) { toast.error(t("recipes.socialShareFailedHint")); return; }
      const a = document.createElement("a");
      a.href = dataUrl; a.download = fname; a.click();
      toast.success(t("recipes.socialDownloaded"));
    } catch (e) {
      toast.error(`${t("common.error")}: ${e?.message || e}`);
    } finally {
      setShareBusy(false);
    }
  };

  if (!recipe && !renderCard) return null;
  const ready = !!png[format];
  const story = format !== "square";
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto overflow-x-hidden rounded-3xl" data-testid="social-share-sheet">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg flex items-center gap-2">
            <Share2 className="h-4 w-4 text-[#5A8B8F]" /> {title || t("recipes.socialShareTitle")}
          </DialogTitle>
        </DialogHeader>

        {fmts.length > 1 && (
          <div>
            <div className="flex gap-1 rounded-full bg-slate-100 p-1" data-testid="social-format-toggle">
              {fmts.map((f) => (
                <button key={f} type="button" onClick={() => setFormat(f)} data-testid={`social-format-${f}`}
                  className={`flex-1 rounded-full py-1.5 text-xs font-semibold transition ${format === f ? "bg-white text-[#2C5254] shadow-sm" : "text-slate-500"}`}>
                  {t(FORMAT_LABEL[f])}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 mt-1.5 px-1" data-testid="social-format-hint">
              {t(FORMAT_HINT[format])}
            </p>
          </div>
        )}

        {/* Preview = the rendered PNG (long-press to save on iOS); offscreen card below is the render source */}
        <div className={`w-full mx-auto rounded-2xl overflow-hidden border border-slate-200 shadow-md bg-[#FAF4E6] ${story ? "max-h-[420px] overflow-y-auto" : ""}`}
          data-testid="social-preview">
          {ready ? (
            <img src={png[format]} alt="" style={{ width: "100%", display: "block" }} data-testid="social-preview-img" />
          ) : renderErr ? (
            <p className="p-4 text-xs text-red-600" data-testid="social-render-error">{t("common.error")}: {renderErr}</p>
          ) : (
            <div className="w-full flex items-center justify-center" style={{ minHeight: 220 }}><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          )}
        </div>
        {isNative && ready && <p className="text-[11px] text-slate-500 -mt-2">{t("recipes.socialLongPressHint")}</p>}
        {recipe && photoNote && photoNote !== "no-image" && (
          <p className="text-[11px] text-amber-600 -mt-2 break-all" data-testid="social-photo-note">{t("recipes.socialPhotoFailed")} ({photoNote})</p>
        )}
        <div ref={cardRef} style={{ position: "fixed", left: -99999, top: 0, pointerEvents: "none" }} aria-hidden>
          {assets && !ready && (renderCard
            ? renderCard(format, assets)
            : <SocialCard recipe={recipe} format={format} labels={labels} assets={assets} />)}
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-bold text-slate-700 flex items-center gap-1"><Sparkles className="h-3 w-3 text-[#E8951C]" /> {t("recipes.socialCaption")}</span>
            <div className="flex gap-1">
              <button type="button" onClick={fetchCaption} disabled={captionBusy} data-testid="social-caption-regen"
                className="h-7 px-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 text-[11px] font-semibold inline-flex items-center gap-1 transition disabled:opacity-60">
                {captionBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} {t("recipes.socialRegenerate")}
              </button>
              <button type="button" onClick={copyCaption} disabled={!caption} data-testid="social-caption-copy"
                className="h-7 px-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 text-[11px] font-semibold inline-flex items-center gap-1 transition disabled:opacity-60">
                <Copy className="h-3 w-3" /> {t("recipes.socialCopy")}
              </button>
            </div>
          </div>
          <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={5} data-testid="social-caption-input"
            placeholder={captionBusy ? t("recipes.socialCaptionBusy") : ""}
            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm leading-snug focus:outline-none focus:ring-2 focus:ring-[#5A8B8F]/40" />
          <p className="text-[11px] text-slate-500 mt-1">{t("recipes.socialCaptionHint")}</p>
        </div>

        <button type="button" onClick={share} disabled={shareBusy || !ready} data-testid="social-share-btn"
          className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-br from-[#833AB4] via-[#E1306C] to-[#F77737] text-white py-3 text-sm font-bold shadow-md hover:opacity-95 active:scale-[0.98] transition disabled:opacity-60">
          {shareBusy || (!ready && !renderErr) ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
          {shareBusy || (!ready && !renderErr) ? t("recipes.socialBusy") : t("recipes.socialShareBtn")}
        </button>
      </DialogContent>
    </Dialog>
  );
}
